import React, { useEffect, useRef, useState } from 'react';
import api, { fileUrl } from '../lib/api';
import StatusBadge from '../components/shared/StatusBadge';
import { useAuth } from '../lib/auth';
import { bcIcon } from '../lib/businessCategories';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, MessageCircle, Inbox, ShieldCheck, Settings as Cog, Power, Repeat, RotateCcw, RefreshCw, AlertCircle, Sparkles, Users, Pencil, Trash2, X, UserPlus, Loader2 } from 'lucide-react';
import GroupActionsMenu from '../components/broadcast/GroupActionsMenu';
import { toast } from 'sonner';
import EmbeddedSignupButton from '../components/whatsapp/EmbeddedSignupButton';

// Feb 2026 — WhatsApp API Integration is ENTERPRISE-ONLY at this stage.
// Non-Enterprise sellers see an upgrade screen; Enterprise sellers (base
// plan or active admin-granted Enterprise promotion) see the existing
// configuration UI unchanged. Access is decided server-side by
// GET /api/whatsapp/access — we mirror that decision here for UI rendering
// only; backend middleware still enforces the same rule.
export default function WhatsAppPage() {
  const [access, setAccess] = useState(null);
  useEffect(() => {
    api.get('/whatsapp/access')
      .then(({ data }) => setAccess(data))
      .catch(() => setAccess({ can_access: false, plan: 'FREE_TRIAL', promotion: null }));
  }, []);
  if (!access) return null;
  return access.can_access ? <WhatsAppConfig access={access} /> : <WhatsAppEnterpriseLock access={access} />;
}

function WhatsAppEnterpriseLock({ access }) {
  const nav = useNavigate();
  const features = [
    'Official WhatsApp Business API',
    'Multiple WhatsApp numbers',
    'Advanced routing',
    'Enterprise messaging infrastructure',
    'Centralized WhatsApp management',
    'Advanced team access',
    'Enterprise support',
  ];
  const promoExpired = access?.promotion?.status === 'EXPIRED' && (access.promotion.plan || '').toUpperCase() === 'ENTERPRISE';
  return (
    <div className="max-w-3xl mx-auto py-6 fade-up" data-testid="whatsapp-enterprise-lock">
      <div className="bg-white border border-fab-primary-lighter rounded-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-fab-primary-lighter to-white p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
              <MessageCircle className="w-6 h-6 text-fab-primary" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold">Enterprise Feature</div>
              <h2 className="font-display font-bold text-fab-text text-2xl" data-testid="whatsapp-lock-title">WhatsApp API Integration</h2>
            </div>
          </div>
          <p className="text-[13px] text-fab-text-secondary mt-3 leading-relaxed max-w-xl">
            Enterprise sellers get direct WhatsApp Business API integration to power advanced WhatsApp sales automation.
          </p>
        </div>
        <div className="p-6 sm:p-8">
          {promoExpired && (
            <div className="mb-5 text-[13px] bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="whatsapp-lock-expired">
              Your Enterprise promotional access has expired. Renew or upgrade to keep WhatsApp API integration available.
            </div>
          )}
          <ul className="space-y-2.5 mb-6" data-testid="whatsapp-lock-features">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[13.5px] text-fab-text">
                <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 text-[12px]">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={() => nav('/dashboard/settings?tab=plan')}
            className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-fab-primary text-white font-semibold text-sm hover:brightness-105"
            data-testid="whatsapp-lock-upgrade-btn"
          >
            Upgrade to Enterprise
          </button>
          <div className="text-[11px] text-fab-text-muted mt-3">
            Current plan: <span className="font-semibold text-fab-text">{access.plan}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function WhatsAppConfig({ access }) {
  const promoEnterprise = access?.promotion?.plan === 'ENTERPRISE'
    && access?.promotion?.status === 'ACTIVE';
  const MODES = [
    { v: 'OFF', label: 'OFF — Manual only', desc: 'AI never replies. All messages go to Inbox for manual reply.' },
    { v: 'WHITELIST', label: 'WHITELIST', desc: 'AI replies only to specific phone numbers you list.' },
    { v: 'BROADCAST_ONLY', label: 'BROADCAST_ONLY', desc: 'AI replies only to customers who received a recent broadcast.' },
    { v: 'TAGGED_ONLY', label: 'TAGGED_ONLY', desc: 'AI replies only to customers with allowed tags (vip / qualified / hot_lead).' },
    { v: 'ALL', label: 'ALL — Reply to everyone', desc: 'AI handles every inbound message (use with caution on live numbers).' },
  ];
  const { seller } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!seller?.is_admin;
  const [numbers, setNumbers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [limits, setLimits] = useState({ used: 0, max: 1, remaining: 1, plan_tier: 'FREE_TRIAL', can_add: true });
  const [tab, setTab] = useState('numbers');
  const [inbox, setInbox] = useState([]);
  const [audit, setAudit] = useState([]);
  const [draftStats, setDraftStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [connectModal, setConnectModal] = useState(null);  // { display_name, phone_number_id, waba_id, access_token, submitting }
  const [replaceModal, setReplaceModal] = useState(null);
  const [provisionModal, setProvisionModal] = useState(null);
  const [groupModal, setGroupModal] = useState(null); // { id?, name, description, type, members:[], ai_enabled, is_active, category_ids:[] }
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Imperative ref → lets the per-card "Reconnect" button launch the same
  // Embedded Signup flow that the top "Connect WhatsApp" button uses.
  const embeddedSignupRef = useRef(null);
  // Surface Meta preflight failures (invalid config_id, app misconfig) so
  // the seller sees the actual blocker instead of a hung "Connecting…" UI.
  const [preflightDiagnostic, setPreflightDiagnostic] = useState(null);

  const load = () => {
    api.get('/whatsapp/numbers').then(({ data }) => setNumbers(data.items));
    api.get('/whatsapp/numbers/limits').then(({ data }) => setLimits(data)).catch(() => {});
    api.get('/whatsapp/groups').then(({ data }) => setGroups(data.items)).catch(() => {});
    // Feb 2026: interested-categories picker shows the seller's Business
    // Categories directly (from auth context) — never expanded into
    // master-category sub-types. `categories` now holds an array of BC name
    // strings, e.g. ["Fabric","Ladies Wear",...].
    setCategories(seller?.business_categories || []);
    api.get('/customers').then(({ data }) => setCustomers(data.items || [])).catch(() => {});
    api.get('/whatsapp/inbox').then(({ data }) => setInbox(data.items));
    api.get('/whatsapp/routing-audit?limit=50').then(({ data }) => setAudit(data.items));
    api.get('/ai/drafts/stats').then(({ data }) => setDraftStats(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [seller]);

  // Deep-link support — /dashboard/whatsapp?tab=groups&edit_group=<gid> from the
  // Broadcast page's ⋮ menu auto-switches to Groups tab and opens the editor.
  useEffect(() => {
    const editGid = searchParams.get('edit_group');
    const wantsTab = searchParams.get('tab');
    if (wantsTab === 'groups') setTab('groups');
    if (editGid && groups.length > 0) {
      const g = groups.find((x) => x.id === editGid);
      if (g) {
        openEditGroupModal(g);
        const clean = new URLSearchParams(searchParams);
        clean.delete('edit_group'); clean.delete('tab');
        setSearchParams(clean, { replace: true });
      }
    }
  }, [groups, searchParams, setSearchParams]);

  const requestConnect = async () => {
    const pnid = (connectModal?.phone_number_id || '').trim();
    const waba = (connectModal?.waba_id || '').trim();
    const tok = (connectModal?.access_token || '').trim();
    if (!pnid || !waba || !tok) {
      return toast.error('phone_number_id, waba_id and access_token are all required');
    }
    setConnectModal({ ...connectModal, submitting: true });
    const startedAt = Date.now();
    try {
      const { data } = await api.post('/whatsapp/numbers/connect', {
        phone_number_id: pnid,
        waba_id: waba,
        access_token: tok,
        display_name: (connectModal?.display_name || '').trim(),
      });
      const elapsed = ((Date.now() - startedAt) / 1000).toFixed(1);
      const warn = data?.activation?.warning;
      if (warn) {
        toast.warning(`Connected in ${elapsed}s, but: ${warn}`);
      } else {
        toast.success(`WhatsApp connected in ${elapsed}s ✓ — broadcasts are live.`);
      }
      setConnectModal(null);
      load();
    } catch (err) {
      setConnectModal((m) => m ? { ...m, submitting: false } : m);
      toast.error(err.response?.data?.detail || err.message || 'Connect failed');
    }
  };

  const disconnect = async (nid) => {
    if (!window.confirm('Disconnect this number? It will stop receiving WhatsApp messages.')) return;
    try {
      await api.post(`/whatsapp/numbers/${nid}/disconnect`);
      toast.success('Number disconnected');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    }
  };

  const reconnect = async (nid) => {
    try {
      const { data } = await api.post(`/whatsapp/numbers/${nid}/reconnect`);
      if (data.status === 'CONNECTED') toast.success('Reconnected ✓');
      else toast.message(data.message || 'Token expired — open the Connect wizard to paste a fresh access token.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconnect failed');
    }
  };

  const submitReplace = async () => {
    if (!replaceModal?.newPhone) return toast.error('Enter the new number');
    try {
      await api.post(`/whatsapp/numbers/${replaceModal.id}/replace`, {
        new_phone_number: replaceModal.newPhone,
        archive_conversations: !!replaceModal.archive,
      });
      toast.success('Number replacement submitted ✓');
      setReplaceModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replace failed');
    }
  };

  const adminReset = async (nid) => {
    if (!window.confirm('ADMIN: Reset this number for testing? Tokens & Meta IDs will be cleared, vendor record kept.')) return;
    try {
      await api.post(`/whatsapp/numbers/${nid}/reset`);
      toast.success('Number reset');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  const adminProvision = async () => {
    if (!provisionModal?.phone_number_id || !provisionModal?.access_token) {
      return toast.error('phone_number_id and access_token required');
    }
    try {
      await api.post(`/admin/whatsapp/numbers/${provisionModal.id}/provision`, {
        phone_number_id: provisionModal.phone_number_id,
        waba_id: provisionModal.waba_id,
        access_token: provisionModal.access_token,
      });
      toast.success('Provisioned ✓ — number is live (AI is paused by Safe-Start)');
      setProvisionModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Provision failed');
    }
  };

  // Groups CRUD
  const submitGroup = async () => {
    const name = (groupModal?.name || '').trim();
    if (!name) return toast.error('Group name required');
    const body = {
      group_name: name,
      description: (groupModal.description || '').trim(),
      type: groupModal.type || 'RESELLER',
      ai_enabled: groupModal.ai_enabled !== false,
      is_active: groupModal.is_active !== false,
      members: (groupModal.members || []).map((m) => m.trim()).filter(Boolean),
      category_ids: groupModal.category_ids || [],
    };
    try {
      if (groupModal.id) {
        await api.patch(`/whatsapp/groups/${groupModal.id}`, body);
        toast.success('Group updated');
      } else {
        await api.post('/whatsapp/groups', body);
        toast.success('Group created');
      }
      setGroupModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const confirmDeleteGroup = async (g) => {
    // Retained for legacy callers (e.g. group-edit modal delete button).
    try {
      await api.delete(`/whatsapp/groups/${g.id}`);
      toast.success('Group deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Helper the shared <GroupActionsMenu> calls to open our full Edit-Group modal.
  const openEditGroupModal = (g) => {
    setGroupModal({
      id: g.id,
      name: g.group_name || g.name || '',
      description: g.description || '',
      type: g.type || 'RESELLER',
      ai_enabled: g.ai_enabled !== false,
      is_active: g.is_active !== false,
      members: [...(g.members || [])],
      category_ids: [...(g.category_ids || [])],
      _phoneInput: '', _customerPickerOpen: false,
    });
  };

  const importCustomersIntoGroup = () => {
    if (!groupModal) return;
    const phones = (customers || []).map((c) => c.phone).filter(Boolean);
    if (!phones.length) return toast.message('No customers yet — add some via WhatsApp inbox or the Customers page.');
    const merged = Array.from(new Set([...(groupModal.members || []), ...phones]));
    setGroupModal({ ...groupModal, members: merged });
    toast.success(`Imported ${phones.length} customers`);
  };

  const saveRouting = async () => {
    try {
      await api.put(`/whatsapp/numbers/${editing.id}/routing`, {
        ai_mode: editing.ai_mode,
        ai_whitelist_phones: editing.ai_whitelist_phones,
        ai_allowed_tags: editing.ai_allowed_tags,
        broadcast_window_days: editing.broadcast_window_days,
      });
      toast.success('Routing saved');
      setEditing(null);
      load();
    } catch (err) {
      toast.error('Save failed');
    }
  };

  const manualReply = async (cid, body) => {
    try {
      await api.post(`/whatsapp/conversations/${cid}/manual-reply`, { body });
      toast.success('Reply sent');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    }
  };

  return (
    <div className="space-y-5 fade-up">
      <div className="flex gap-2 border-b border-fab-primary-lighter overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0 no-scrollbar">
        {[['numbers', 'Numbers'], ['groups', `Groups${groups.length ? ` (${groups.length})` : ''}`], ['routing', 'Routing'], ['inbox', `Inbox${inbox.length ? ` (${inbox.length})` : ''}`], ['audit', 'Audit Log']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 sm:px-4 py-2 text-[13px] font-semibold border-b-2 -mb-px shrink-0 whitespace-nowrap ${tab === k ? 'text-fab-primary border-fab-primary' : 'text-fab-text-muted border-transparent'}`} data-testid={`tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === 'numbers' && (
        <div className="space-y-4">
          {/* Usage chip + Add CTA */}
          <div className="fab-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-display font-bold text-fab-text">WhatsApp Business Numbers</div>
              <div className="text-[12px] text-fab-text-muted mt-1 flex items-center gap-2 flex-wrap">
                <span data-testid="wa-usage-chip" className={`px-2 py-0.5 rounded-full font-mono text-[11px] font-semibold ${limits.can_add ? 'bg-emerald-50 text-emerald-700 border border-emerald-300' : 'bg-orange-50 text-orange-700 border border-orange-300'}`}>
                  {limits.used} / {limits.max} connected
                </span>
                <span className="text-fab-text-muted">on</span>
                <span className="font-mono font-bold text-fab-primary">{limits.plan_tier}</span>
                {promoEnterprise && (
                  <span
                    data-testid="wa-enterprise-promo-badge"
                    className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-300"
                    title={`Enterprise access via active promotion${access?.promotion?.expiry_date ? ` (until ${access.promotion.expiry_date.slice(0, 10)})` : ''}`}
                  >
                    Enterprise Access via Promotion
                  </span>
                )}
                {!limits.can_add && (
                  <Link to="/pricing" data-testid="wa-upgrade-cta" className="ml-2 text-fab-primary font-semibold underline">Upgrade for more numbers →</Link>
                )}
              </div>
            </div>
            <EmbeddedSignupButton
              ref={embeddedSignupRef}
              onConnected={load}
              onPreflightFail={(d) => setPreflightDiagnostic(d)}
              // Allow click whenever the seller has at least one slot OR there's
              // any reusable slot (AWAITING_RECONNECT / PENDING_PROVISION /
              // DISCONNECTED) that the activation pipeline can overwrite.
              disabled={!limits.can_add && !numbers.some(n => ['AWAITING_RECONNECT','PENDING_PROVISION','DISCONNECTED'].includes(n.status))}
              fallback={(
                <button
                  data-testid="wa-add-number-btn"
                  onClick={() => limits.can_add && setConnectModal({ display_name: '', phone_number_id: '', waba_id: '', access_token: '' })}
                  disabled={!limits.can_add}
                  className={`btn-wa px-4 py-2 rounded-lg font-semibold text-[13px] inline-flex items-center gap-1.5 ${limits.can_add ? '' : 'opacity-50 cursor-not-allowed'}`}
                  title={limits.can_add ? 'Add a WhatsApp Business number' : `You've reached the ${limits.max}-number limit. Upgrade to add more.`}
                >
                  <Plus className="w-4 h-4" /> Connect WhatsApp
                </button>
              )}
            />
          </div>

          {/* Meta misconfiguration banner — replaces the "stuck Connecting…" UX */}
          {preflightDiagnostic && preflightDiagnostic.ok === false && (
            <div data-testid="wa-preflight-banner" className="border border-red-300 bg-red-50 rounded-xl p-4 text-[13px] text-red-900">
              <div className="font-semibold mb-1 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Meta Embedded Signup configuration error
              </div>
              <div className="text-[12.5px]">{preflightDiagnostic.diagnostic}</div>
              <div className="mt-2 font-mono text-[11px] text-red-800">
                META_APP_ID = {preflightDiagnostic.app_id || '(missing)'} ·
                META_LOGIN_CONFIG_ID = {preflightDiagnostic.config_id || '(missing)'}
              </div>
              <div className="mt-2 text-[11.5px]">
                Until this is fixed, the &quot;Connect WhatsApp&quot; button is disabled. Use the
                fallback paste-credentials wizard or update <code className="font-mono bg-white px-1 rounded">META_LOGIN_CONFIG_ID</code> in your environment.
              </div>
            </div>
          )}

          {numbers.length === 0 && (
            <div className="fab-card p-10 text-center text-fab-text-muted">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
              No WhatsApp numbers yet. Click <strong>Connect WhatsApp</strong> to add your first business number.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {numbers.map((n) => {
              // Don't default-to-CONNECTED — that caused the WhatsApp page to
              // claim a number was Connected while the broadcast engine knew
              // it wasn't. Treat missing status as a pending record.
              const status = n.status || 'PENDING_PROVISION';
              const isActive = status === 'CONNECTED' && n.meta_validated === true;
              const isPending = status === 'PENDING_PROVISION' || status === 'AWAITING_RECONNECT' || (status === 'CONNECTED' && !n.meta_validated);
              const isDisconnected = status === 'DISCONNECTED';
              const isArchived = status === 'ARCHIVED';
              return (
                <div key={n.id} className="fab-card p-5 space-y-3" data-testid={`wa-number-card-${n.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${isActive ? 'bg-fab-wa' : 'bg-fab-text-muted'}`}>
                      <MessageCircle className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-fab-text text-[14px] truncate">{n.phone_number || '—'}</div>
                      <div className="text-[11px] text-fab-text-muted truncate">{n.display_name}</div>
                    </div>
                    <StatusBadge status={isActive ? 'CONNECTED' : (n.meta_validated === false && status === 'CONNECTED' ? 'PENDING_PROVISION' : status)} size="sm" />
                  </div>

                  {isPending && (
                    <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2 flex items-start gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        {status === 'AWAITING_RECONNECT'
                          ? 'Token expired or revoked. Click Reconnect to paste a fresh access token from Meta Business Suite.'
                          : 'Awaiting credentials. Click Connect WhatsApp to finish self-serve activation (< 60 seconds).'}
                      </span>
                    </div>
                  )}

                  {isActive && (
                    <div className="text-[12px] flex items-center justify-between">
                      <span>AI mode: <strong className={`font-mono ${n.ai_mode === 'OFF' ? 'text-fab-error' : n.ai_mode === 'ALL' ? 'text-fab-success' : 'text-fab-warning'}`}>{n.ai_mode || 'OFF'}</strong></span>
                      <button onClick={() => setEditing({ ...n, ai_mode: n.ai_mode || 'OFF', ai_whitelist_phones: n.ai_whitelist_phones || [], ai_allowed_tags: n.ai_allowed_tags || ['vip', 'qualified', 'hot_lead'], broadcast_window_days: n.broadcast_window_days || 7 })} data-testid={`configure-${n.id}`} className="text-fab-primary text-[12px] font-semibold inline-flex items-center gap-1"><Cog className="w-3 h-3" /> Routing</button>
                    </div>
                  )}

                  {/* Vendor actions */}
                  <div className="flex flex-wrap gap-2 pt-1 border-t border-fab-primary-lighter">
                    {(isActive || isPending) && (
                      <button data-testid={`wa-edit-${n.id}`} onClick={() => setEditing({ ...n, ai_mode: n.ai_mode || 'ALL', ai_whitelist_phones: n.ai_whitelist_phones || [], ai_allowed_tags: n.ai_allowed_tags || ['vip', 'qualified', 'hot_lead'], broadcast_window_days: n.broadcast_window_days || 7 })}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded border border-fab-primary-lighter text-fab-text hover:bg-fab-elevated inline-flex items-center gap-1">
                        <Cog className="w-3 h-3" /> Edit
                      </button>
                    )}
                    {(isDisconnected || status === 'AWAITING_RECONNECT' || status === 'PENDING_PROVISION') && (
                      <button data-testid={`wa-reconnect-${n.id}`}
                        onClick={() => {
                          // If Embedded Signup is live + ready, route the
                          // Reconnect button through it — that's the natural
                          // recovery path and avoids forcing the seller back
                          // into the credential-paste modal.
                          if (embeddedSignupRef.current?.isReady?.()) {
                            embeddedSignupRef.current.launch();
                            return;
                          }
                          setConnectModal({
                            display_name: n.display_name || '',
                            phone_number_id: n.phone_number_id || '',
                            waba_id: n.waba_id || '',
                            access_token: '',
                            existing_id: n.id,
                            existing_phone: n.phone_number || '',
                            mode: 'reconnect',
                          });
                        }}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded border border-fab-wa text-fab-wa-dark hover:bg-fab-wa-light inline-flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" /> {status === 'PENDING_PROVISION' ? 'Activate' : 'Reconnect'}
                      </button>
                    )}
                    {(isActive || isPending) && (
                      <button data-testid={`wa-disconnect-${n.id}`} onClick={() => disconnect(n.id)}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1">
                        <Power className="w-3 h-3" /> Disconnect
                      </button>
                    )}
                    {!isArchived && (
                      <button data-testid={`wa-replace-${n.id}`} onClick={() => setReplaceModal({ id: n.id, oldPhone: n.phone_number, newPhone: '', archive: true })}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded border border-fab-primary-lighter text-fab-text hover:bg-fab-elevated inline-flex items-center gap-1">
                        <Repeat className="w-3 h-3" /> Replace
                      </button>
                    )}
                    {isAdmin && (
                      <>
                        {(isPending) && (
                          <button data-testid={`wa-provision-${n.id}`} onClick={() => setProvisionModal({ id: n.id, phone: n.phone_number, phone_number_id: '', waba_id: '', access_token: '' })}
                            className="text-[11px] font-semibold px-2.5 py-1 rounded bg-fab-primary text-white hover:bg-fab-primary-dark inline-flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Provision (admin)
                          </button>
                        )}
                        <button data-testid={`wa-reset-${n.id}`} onClick={() => adminReset(n.id)}
                          className="text-[11px] font-semibold px-2.5 py-1 rounded border border-amber-300 text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1">
                          <RotateCcw className="w-3 h-3" /> Reset (admin)
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'groups' && (
        <div className="space-y-4">
          <div className="fab-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display font-bold text-fab-text flex items-center gap-2"><Users className="w-4 h-4 text-fab-primary shrink-0" /> Broadcast Groups & Lists</div>
              <div className="text-[12px] text-fab-text-muted mt-1">Create reusable buyer lists. Use them in <strong>Catalog Broadcast</strong> to send products to many buyers at once.</div>
            </div>
            <button data-testid="wa-add-group-btn" onClick={() => setGroupModal({ name: '', description: '', type: 'RESELLER', ai_enabled: true, is_active: true, members: [], category_ids: [], _phoneInput: '', _customerPickerOpen: false })} className="btn-primary text-[13px] inline-flex items-center justify-center gap-1.5 w-full sm:w-auto shrink-0"><Plus className="w-4 h-4" /> Add Group</button>
          </div>

          {groups.length === 0 && (
            <div className="fab-card p-10 text-center text-fab-text-muted">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
              No groups yet. Click <strong>+ Add Group</strong> to create your first buyer list.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((g) => (
              <div key={g.id} className="fab-card p-5 space-y-3 relative" data-testid={`wa-group-card-${g.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-fab-text text-[14px] truncate">{g.group_name || g.name || 'Untitled group'}</div>
                    <div className="text-[11px] text-fab-text-muted">{g.type} · {g.member_count || (g.members || []).length} members</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold ${g.ai_enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-300' : 'bg-gray-100 text-gray-600 border border-gray-300'}`}>{g.ai_enabled ? 'AI ON' : 'AI OFF'}</span>
                    <GroupActionsMenu group={g} onEdit={openEditGroupModal} onChanged={load} />
                  </div>
                </div>
                {(g.category_names || []).length > 0 && (
                  <div className="flex flex-wrap gap-1" data-testid={`wa-group-cats-${g.id}`}>
                    {(g.category_names || []).map((cn) => (
                      <span key={cn} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-fab-primary-lighter text-fab-primary border border-fab-primary/20">{cn}</span>
                    ))}
                  </div>
                )}
                {(g.members || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                    {(g.members || []).slice(0, 6).map((p) => (
                      <span key={p} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-fab-elevated border border-fab-primary-lighter">{p}</span>
                    ))}
                    {(g.members || []).length > 6 && <span className="text-[10px] text-fab-text-muted">+{g.members.length - 6} more</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'routing' && (
        <div className="fab-card p-5">
          <div className="font-display font-bold text-fab-text mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-fab-primary" /> Routing Mode Quick-Reference</div>
          <div className="space-y-2">
            {MODES.map((m) => (
              <div key={m.v} className="p-3 rounded-lg bg-fab-elevated">
                <div className="font-mono font-bold text-[12px] text-fab-primary">{m.label}</div>
                <div className="text-[12px] text-fab-text-secondary">{m.desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-[12px] text-fab-text-muted">To change a number&apos;s mode, go to the <strong>Numbers</strong> tab and click <strong>Configure</strong>.</div>
        </div>
      )}

      {tab === 'inbox' && (
        <div className="space-y-3">
          {draftStats && draftStats.total > 0 && (
            <div className="fab-card p-3 flex flex-wrap items-center gap-3 sm:gap-6 text-[12px]" data-testid="draft-stats-banner">
              <span className="font-semibold text-fab-text">✨ AI Draft accuracy</span>
              <span className="text-emerald-700"><strong>{Math.round((draftStats.approval_rate || 0) * 100)}%</strong> approved</span>
              <span className="text-fab-warning"><strong>{Math.round((draftStats.edit_rate || 0) * 100)}%</strong> edited</span>
              <span className="text-fab-error"><strong>{Math.round((draftStats.rejection_rate || 0) * 100)}%</strong> rejected</span>
              {draftStats.avg_similarity !== null && (
                <span className="text-fab-text-muted ml-auto">avg similarity: <strong>{(draftStats.avg_similarity * 100).toFixed(0)}%</strong></span>
              )}
              <span className="text-fab-text-muted">total: {draftStats.total}</span>
            </div>
          )}
          {inbox.length === 0 && <div className="fab-card p-8 text-center text-fab-text-muted"><Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" /> Inbox empty. AI is handling allowed conversations.</div>}
          {inbox.map((c) => <InboxRow key={c.id} conv={c} onReply={manualReply} />)}
        </div>
      )}

      {tab === 'audit' && (
        <div className="fab-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-[12px] min-w-[560px]">
            <thead><tr className="text-[10px] text-fab-text-muted uppercase tracking-wider bg-fab-elevated">
              <th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">From</th><th className="text-left px-3 py-2">Mode</th><th className="text-left px-3 py-2">Allowed?</th><th className="text-left px-3 py-2">Reason</th>
            </tr></thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-b border-fab-primary-lighter last:border-0">
                  <td className="px-3 py-2 text-fab-text-muted">{new Date(a.timestamp).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono">{a.customer_phone}</td>
                  <td className="px-3 py-2 font-mono">{a.mode}</td>
                  <td className="px-3 py-2">{a.allowed ? <span className="text-fab-success font-semibold">✓ AI replied</span> : <span className="text-fab-error font-semibold">✗ suppressed</span>}</td>
                  <td className="px-3 py-2 font-mono text-fab-text-muted">{a.reason}</td>
                </tr>
              ))}
              {audit.length === 0 && <tr><td colSpan="5" className="text-center text-fab-text-muted py-8">No routing decisions logged yet.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Self-serve Connect WhatsApp wizard — < 60 second activation */}
      {connectModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => !connectModal.submitting && setConnectModal(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wa-connect-modal">
            <div className="flex items-start justify-between mb-1">
              <div>
                <div className="font-display font-bold text-fab-text text-lg">
                  {connectModal.mode === 'reconnect' ? 'Reconnect WhatsApp Business' : 'Connect WhatsApp Business'}
                </div>
                <div className="text-[12px] text-fab-text-muted">
                  {connectModal.mode === 'reconnect'
                    ? `Paste a fresh access token for ${connectModal.existing_phone || 'this number'} to reactivate.`
                    : 'Self-serve activation — no FABTEXS team approval required. Typical time: 30–60 seconds.'}
                </div>
              </div>
              <button onClick={() => !connectModal.submitting && setConnectModal(null)} className="text-fab-text-muted hover:text-fab-text" disabled={connectModal.submitting}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 3-step instructions */}
            <div className="mt-4 mb-4 border border-fab-primary-lighter rounded-lg p-3 bg-fab-elevated/40 space-y-2 text-[12px]">
              <div className="font-semibold text-fab-text text-[12px] uppercase tracking-wider">Get your Meta credentials</div>
              <ol className="list-decimal pl-5 space-y-1 text-fab-text-secondary">
                <li>Open <a href="https://business.facebook.com/wa/manage/home/" target="_blank" rel="noreferrer" className="text-fab-primary font-semibold underline">Meta Business Suite → WhatsApp Manager</a> in a new tab.</li>
                <li>Pick your number → <strong>API Setup</strong> tab. Copy the <strong>Phone number ID</strong> and <strong>WhatsApp Business Account ID</strong>.</li>
                <li>Under <strong>Temporary access token</strong> click <em>Generate</em> (24h) for quick test, or open <strong>System Users</strong> in Business Settings to mint a permanent token (recommended for production).</li>
              </ol>
              <div className="text-[11px] text-fab-text-muted pt-1 border-t border-fab-primary-lighter">
                Need long-lived tokens? Create a System User with the <code className="font-mono text-[10px] bg-white px-1 rounded">whatsapp_business_messaging</code> + <code className="font-mono text-[10px] bg-white px-1 rounded">whatsapp_business_management</code> permissions, then generate a token that never expires.
              </div>
            </div>

            <label className="text-[12px] font-semibold text-fab-text">Display Name <span className="text-fab-text-muted font-normal">(optional — defaults to your verified name on Meta)</span></label>
            <input
              data-testid="wa-connect-name-input"
              value={connectModal.display_name}
              onChange={(e) => setConnectModal({ ...connectModal, display_name: e.target.value })}
              placeholder="Ramesh Textiles"
              disabled={connectModal.submitting}
              className="fab-input mt-1 mb-3 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
            />

            <label className="text-[12px] font-semibold text-fab-text">Phone Number ID <span className="text-red-500">*</span></label>
            <input
              data-testid="wa-connect-pnid-input"
              autoFocus={!connectModal.phone_number_id}
              value={connectModal.phone_number_id}
              onChange={(e) => setConnectModal({ ...connectModal, phone_number_id: e.target.value })}
              placeholder="e.g. 1154861301044802"
              disabled={connectModal.submitting}
              className="fab-input mt-1 mb-3 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono"
            />

            <label className="text-[12px] font-semibold text-fab-text">WhatsApp Business Account ID (WABA) <span className="text-red-500">*</span></label>
            <input
              data-testid="wa-connect-waba-input"
              value={connectModal.waba_id}
              onChange={(e) => setConnectModal({ ...connectModal, waba_id: e.target.value })}
              placeholder="e.g. 861038836564468"
              disabled={connectModal.submitting}
              className="fab-input mt-1 mb-3 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono"
            />

            <label className="text-[12px] font-semibold text-fab-text">Access Token <span className="text-red-500">*</span></label>
            <input
              data-testid="wa-connect-token-input"
              type="password"
              autoFocus={!!connectModal.phone_number_id}
              value={connectModal.access_token}
              onChange={(e) => setConnectModal({ ...connectModal, access_token: e.target.value })}
              placeholder="EAAxxxxxxxxxxxxxxxx (System User permanent token recommended)"
              disabled={connectModal.submitting}
              className="fab-input mt-1 mb-2 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono"
            />
            <div className="text-[11px] text-fab-text-muted mb-4">
              🔒 Your token is encrypted at rest with AES-256 and is only used to call Meta on your behalf. It never leaves FABTEXS.
            </div>

            <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-4 space-y-1">
              <div className="font-semibold">When you click Activate, FABTEXS will automatically:</div>
              <ul className="list-disc pl-4 text-emerald-800">
                <li>Validate your credentials with Meta</li>
                <li>Register your phone on Cloud API</li>
                <li>Subscribe webhooks so inbound messages reach your inbox</li>
                <li>Switch AI Autopilot ON so replies happen 24×7</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => !connectModal.submitting && setConnectModal(null)}
                disabled={connectModal.submitting}
                className="px-4 py-2 text-[13px] text-fab-text-secondary font-semibold disabled:opacity-50"
              >Cancel</button>
              <button
                data-testid="wa-connect-submit"
                onClick={requestConnect}
                disabled={connectModal.submitting}
                className="btn-wa text-[13px] px-4 py-2 rounded-lg font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {connectModal.submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Activating…</>) : (<><Sparkles className="w-4 h-4" /> Activate WhatsApp</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace modal */}
      {replaceModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setReplaceModal(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wa-replace-modal">
            <div className="font-display font-bold text-fab-text text-lg mb-1">Replace Number</div>
            <div className="text-[12px] text-fab-text-muted mb-4">Current: <span className="font-mono">{replaceModal.oldPhone || '—'}</span>. This archives the old number; you can self-activate the new one via the Connect wizard in &lt; 60 seconds.</div>
            <label className="text-[12px] font-semibold text-fab-text">New WhatsApp Business Number</label>
            <input
              data-testid="wa-replace-newphone-input"
              autoFocus
              value={replaceModal.newPhone}
              onChange={(e) => setReplaceModal({ ...replaceModal, newPhone: e.target.value })}
              placeholder="+91 99…"
              className="fab-input mt-1 mb-3 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[14px]"
            />
            <label className="flex items-center gap-2 text-[12px] mb-4 cursor-pointer">
              <input type="checkbox" checked={!!replaceModal.archive} onChange={(e) => setReplaceModal({ ...replaceModal, archive: e.target.checked })} className="accent-fab-primary" data-testid="wa-replace-archive-toggle" />
              <span>Archive old conversations (recommended)</span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setReplaceModal(null)} className="px-4 py-2 text-[13px] text-fab-text-secondary font-semibold">Cancel</button>
              <button data-testid="wa-replace-submit" onClick={submitReplace} className="btn-primary text-[13px]">Replace Number</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin provision modal (interim flow until Embedded Signup ships) */}
      {provisionModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setProvisionModal(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wa-provision-modal">
            <div className="font-display font-bold text-fab-text text-lg mb-1">Admin: Provision Meta Credentials</div>
            <div className="text-[12px] text-fab-text-muted mb-1">For <span className="font-mono">{provisionModal.phone}</span> — paste the Meta technical IDs to activate this number.</div>
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4">
              ⚠ Vendors never see these fields. This is a FABTEXS-team-only step until Meta Embedded Signup approval completes.
            </div>
            <div className="space-y-2">
              <input data-testid="wa-prov-pnid" value={provisionModal.phone_number_id} onChange={(e) => setProvisionModal({ ...provisionModal, phone_number_id: e.target.value })} placeholder="phone_number_id" className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono" />
              <input data-testid="wa-prov-waba" value={provisionModal.waba_id} onChange={(e) => setProvisionModal({ ...provisionModal, waba_id: e.target.value })} placeholder="waba_id" className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono" />
              <input data-testid="wa-prov-token" type="password" value={provisionModal.access_token} onChange={(e) => setProvisionModal({ ...provisionModal, access_token: e.target.value })} placeholder="access_token (system user)" className="fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setProvisionModal(null)} className="px-4 py-2 text-[13px] text-fab-text-secondary font-semibold">Cancel</button>
              <button data-testid="wa-prov-submit" onClick={adminProvision} className="btn-primary text-[13px]">Validate & Activate</button>
            </div>
          </div>
        </div>
      )}

      {/* Group create / edit modal */}
      {groupModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setGroupModal(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wa-group-modal">
            <div className="flex items-center justify-between mb-1">
              <div className="font-display font-bold text-fab-text text-lg">{groupModal.id ? 'Edit Group' : 'Create Broadcast Group'}</div>
              <button onClick={() => setGroupModal(null)} className="text-fab-text-muted hover:text-fab-text"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-[12px] text-fab-text-muted mb-4">Add buyer WhatsApp numbers (with country code). Use this list when sending catalog broadcasts.</div>

            <label className="text-[12px] font-semibold text-fab-text">Group Name</label>
            <input data-testid="wa-group-name-input" autoFocus value={groupModal.name}
              onChange={(e) => setGroupModal({ ...groupModal, name: e.target.value })}
              placeholder="e.g. Surat Resellers, Tier-1 Buyers, Mumbai VIPs"
              className="fab-input mt-1 mb-3 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[14px]" />

            <label className="text-[12px] font-semibold text-fab-text">Description <span className="font-normal text-fab-text-muted">(optional)</span></label>
            <textarea
              data-testid="wa-group-description-input"
              value={groupModal.description || ''}
              onChange={(e) => setGroupModal({ ...groupModal, description: e.target.value })}
              placeholder="Short internal note about this group — e.g. weekly bulk buyers who prefer cotton kurtis"
              rows={2}
              className="fab-input mt-1 mb-3 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] resize-y"
            />

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[12px] font-semibold text-fab-text">Group Type</label>
                <select data-testid="wa-group-type-select" value={groupModal.type}
                  onChange={(e) => setGroupModal({ ...groupModal, type: e.target.value })}
                  className="fab-input mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]">
                  <option value="RESELLER">Resellers</option>
                  <option value="RETAIL">Retail Buyers</option>
                  <option value="VIP">VIP / Tier-1</option>
                  <option value="COLD">Cold leads</option>
                  <option value="CUSTOM">Custom</option>
                </select>
              </div>
              <div className="flex flex-col gap-1 pt-1">
                <label className="flex items-center gap-2 text-[12px] cursor-pointer">
                  <input type="checkbox" data-testid="wa-group-ai-toggle" checked={!!groupModal.ai_enabled}
                    onChange={(e) => setGroupModal({ ...groupModal, ai_enabled: e.target.checked })}
                    className="accent-fab-primary" />
                  <span>AI replies allowed for this group</span>
                </label>
                <label className="flex items-center gap-2 text-[12px] cursor-pointer">
                  <input type="checkbox" data-testid="wa-group-active-toggle" checked={groupModal.is_active !== false}
                    onChange={(e) => setGroupModal({ ...groupModal, is_active: e.target.checked })}
                    className="accent-fab-primary" />
                  <span>Group is active</span>
                </label>
              </div>
            </div>

            {/* Interested categories — driven by seller's Business Categories */}
            {categories.length > 0 && (
              <div className="mb-4" data-testid="wa-group-cat-section">
                <label className="text-[12px] font-semibold text-fab-text">Interested categories</label>
                <div className="text-[11px] text-fab-text-muted mb-1.5">Tag the products this group cares about. Used for category-aware broadcasts &amp; AI replies.</div>
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((c) => {
                    const selected = (groupModal.category_ids || []).includes(c);
                    const slug = (c || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                    return (
                      <button
                        key={c}
                        type="button"
                        data-testid={`wa-group-cat-${slug}`}
                        onClick={() => setGroupModal({
                          ...groupModal,
                          category_ids: selected
                            ? (groupModal.category_ids || []).filter((x) => x !== c)
                            : [...(groupModal.category_ids || []), c]
                        })}
                        className={`px-2.5 py-1 rounded-full border text-[12px] font-semibold transition-colors ${selected ? 'bg-fab-primary text-white border-fab-primary' : 'bg-white text-fab-text-secondary border-fab-primary-lighter hover:border-fab-primary'}`}
                      >
                        <span className="mr-1">{bcIcon(c)}</span>{c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <label className="text-[12px] font-semibold text-fab-text flex items-center justify-between">
              <span>Buyer Phone Numbers ({(groupModal.members || []).length})</span>
              <div className="flex items-center gap-3">
                {customers.length > 0 && (
                  <button
                    type="button"
                    data-testid="wa-group-pick-customers"
                    onClick={() => setGroupModal({ ...groupModal, _customerPickerOpen: !groupModal._customerPickerOpen, _customerQuery: '' })}
                    className="text-[11px] font-semibold text-fab-primary inline-flex items-center gap-1 hover:underline"
                  >
                    <UserPlus className="w-3 h-3" /> {groupModal._customerPickerOpen ? 'Close picker' : 'Pick from customers'}
                  </button>
                )}
                {customers.length > 0 && (
                  <button data-testid="wa-group-import-customers" onClick={importCustomersIntoGroup} className="text-[11px] font-semibold text-fab-text-secondary inline-flex items-center gap-1 hover:underline">
                    Import all {customers.length}
                  </button>
                )}
              </div>
            </label>
            <div className="mt-1 mb-2 flex gap-2">
              <input data-testid="wa-group-phone-input" value={groupModal._phoneInput || ''}
                onChange={(e) => setGroupModal({ ...groupModal, _phoneInput: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    const phones = (groupModal._phoneInput || '').split(/[,\n\s]+/).map((p) => p.trim()).filter(Boolean);
                    if (phones.length === 0) return;
                    const merged = Array.from(new Set([...(groupModal.members || []), ...phones]));
                    setGroupModal({ ...groupModal, members: merged, _phoneInput: '' });
                  }
                }}
                placeholder="+91 98765 43210  (press Enter or comma to add)"
                className="fab-input flex-1 px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]" />
              <button data-testid="wa-group-add-phone-btn" onClick={() => {
                const phones = (groupModal._phoneInput || '').split(/[,\n\s]+/).map((p) => p.trim()).filter(Boolean);
                if (phones.length === 0) return;
                const merged = Array.from(new Set([...(groupModal.members || []), ...phones]));
                setGroupModal({ ...groupModal, members: merged, _phoneInput: '' });
              }} className="px-3 py-2 rounded-lg bg-fab-primary text-white text-[12px] font-semibold">Add</button>
            </div>

            {groupModal._customerPickerOpen && customers.length > 0 && (
              <div className="border border-fab-primary-lighter rounded-lg p-2 mb-3 bg-white" data-testid="wa-group-customer-picker">
                <input
                  data-testid="wa-group-customer-search"
                  value={groupModal._customerQuery || ''}
                  onChange={(e) => setGroupModal({ ...groupModal, _customerQuery: e.target.value })}
                  placeholder="Search customer name, phone, city…"
                  className="w-full px-2 py-1.5 mb-2 rounded border border-fab-primary-lighter text-[12px] focus:outline-none focus:border-fab-primary"
                />
                <div className="max-h-40 overflow-y-auto divide-y divide-fab-primary-lighter">
                  {customers
                    .filter((c) => {
                      const q = (groupModal._customerQuery || '').toLowerCase().trim();
                      if (!q) return true;
                      return (c.name || '').toLowerCase().includes(q)
                        || (c.phone || '').toLowerCase().includes(q)
                        || (c.city || '').toLowerCase().includes(q);
                    })
                    .slice(0, 100)
                    .map((c) => {
                      const isMember = (groupModal.members || []).includes(c.phone);
                      return (
                        <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-fab-elevated cursor-pointer text-[12px]">
                          <input
                            type="checkbox"
                            data-testid={`wa-group-cust-${c.id}`}
                            checked={isMember}
                            onChange={() => {
                              const current = new Set(groupModal.members || []);
                              if (isMember) current.delete(c.phone); else current.add(c.phone);
                              setGroupModal({ ...groupModal, members: Array.from(current) });
                            }}
                            className="accent-fab-primary"
                          />
                          <span className="font-semibold text-fab-text flex-1 truncate">{c.name || <span className="text-fab-text-muted italic">(no name)</span>}</span>
                          <span className="font-mono text-fab-text-secondary">{c.phone}</span>
                        </label>
                      );
                    })}
                  {customers.filter((c) => {
                    const q = (groupModal._customerQuery || '').toLowerCase().trim();
                    if (!q) return true;
                    return (c.name || '').toLowerCase().includes(q) || (c.phone || '').toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q);
                  }).length === 0 && (
                    <div className="px-2 py-3 text-center text-fab-text-muted text-[12px]">No customers match.</div>
                  )}
                </div>
              </div>
            )}

            {(groupModal.members || []).length > 0 && (
              <div className="border border-fab-primary-lighter rounded-lg p-2 max-h-40 overflow-y-auto bg-fab-elevated mb-4" data-testid="wa-group-members-list">
                <div className="flex flex-wrap gap-1.5">
                  {(groupModal.members || []).map((p) => (
                    <span key={p} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white border border-fab-primary-lighter inline-flex items-center gap-1">
                      {p}
                      <button onClick={() => setGroupModal({ ...groupModal, members: (groupModal.members || []).filter((x) => x !== p) })}
                        className="text-red-500 hover:text-red-700 text-[10px]"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4 sticky bottom-0 bg-white pt-2">
              <button onClick={() => setGroupModal(null)} className="px-4 py-2 text-[13px] text-fab-text-secondary font-semibold">Cancel</button>
              <button data-testid="wa-group-submit" onClick={submitGroup} className="btn-primary text-[13px]">{groupModal.id ? 'Save changes' : 'Create Group'}</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-xl w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-bold text-fab-text text-lg mb-1">Configure AI Routing</div>
            <div className="text-[12px] text-fab-text-muted mb-4">{editing.phone_number || editing.phone_number_id}</div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {MODES.map((m) => (
                <label key={m.v} className={`block p-3 rounded-lg border-2 cursor-pointer ${editing.ai_mode === m.v ? 'border-fab-primary bg-fab-primary-lighter' : 'border-fab-primary-lighter'}`}>
                  <div className="flex items-center gap-2">
                    <input type="radio" checked={editing.ai_mode === m.v} onChange={() => setEditing({ ...editing, ai_mode: m.v })} className="accent-fab-primary" data-testid={`mode-${m.v}`} />
                    <span className="font-mono font-bold text-[12px]">{m.label}</span>
                  </div>
                  <div className="text-[11px] text-fab-text-secondary mt-1 ml-6">{m.desc}</div>
                </label>
              ))}
              {editing.ai_mode === 'WHITELIST' && (
                <div className="pt-2">
                  <label className="text-[12px] font-semibold">Allowed phones (comma-separated, with country code)</label>
                  <textarea value={(editing.ai_whitelist_phones || []).join(', ')} onChange={(e) => setEditing({ ...editing, ai_whitelist_phones: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="+919876543210, +919876500999" className="fab-input mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[12px]" rows={3} data-testid="whitelist-input" />
                </div>
              )}
              {editing.ai_mode === 'TAGGED_ONLY' && (
                <div className="pt-2">
                  <label className="text-[12px] font-semibold">Allowed tags (comma-separated)</label>
                  <input value={(editing.ai_allowed_tags || []).join(', ')} onChange={(e) => setEditing({ ...editing, ai_allowed_tags: e.target.value.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) })} placeholder="vip, qualified, hot_lead" className="fab-input mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[12px]" />
                </div>
              )}
              {editing.ai_mode === 'BROADCAST_ONLY' && (
                <div className="pt-2">
                  <label className="text-[12px] font-semibold">Broadcast response window (days)</label>
                  <input type="number" min="1" max="90" value={editing.broadcast_window_days || 7} onChange={(e) => setEditing({ ...editing, broadcast_window_days: parseInt(e.target.value) || 7 })} className="fab-input mt-1 w-32 px-3 py-2 rounded-lg border border-fab-primary-lighter text-[12px]" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-[13px] text-fab-text-secondary font-semibold">Cancel</button>
              <button onClick={saveRouting} className="btn-primary text-[13px]" data-testid="save-routing">Save Routing</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InboxRow({ conv, onReply }) {
  const [body, setBody] = useState('');
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const li = conv.last_intent || {};
  const intentColors = {
    BUSINESS_INQUIRY: 'bg-emerald-50 text-emerald-700 border-emerald-300',
    ORDER_STATUS: 'bg-sky-50 text-sky-700 border-sky-300',
    HIGH_VALUE_LEAD: 'bg-amber-50 text-amber-800 border-amber-300',
    COMPLAINT_OR_NEGOTIATION: 'bg-orange-50 text-orange-700 border-orange-300',
    CUSTOM_ORDER: 'bg-violet-50 text-violet-700 border-violet-300',
    PERSONAL_CONVERSATION: 'bg-slate-100 text-slate-600 border-slate-300',
    GREETING: 'bg-slate-100 text-slate-600 border-slate-300',
    UNCLEAR: 'bg-gray-100 text-gray-600 border-gray-300',
  };
  const chipCls = intentColors[li.intent] || 'bg-gray-100 text-gray-600 border-gray-300';
  const aiAssistable = li.intent && ['BUSINESS_INQUIRY', 'ORDER_STATUS'].includes(li.intent);

  const generateDraft = async () => {
    setLoadingAction('generate');
    try {
      const { data } = await api.post(`/whatsapp/conversations/${conv.id}/draft`, {});
      setDraft(data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate draft');
    } finally { setLoadingAction(null); }
  };
  const approveDraft = async (finalText, override = false) => {
    setLoadingAction('approve');
    try {
      const body = { final_reply: finalText ?? draft.suggested_reply };
      if (override) body.override_validator = true;
      const { data } = await api.post(`/ai/drafts/${draft.id}/approve`, body);
      toast.success(data.status === 'EDITED'
        ? `Sent (edited, ${Math.round((1 - data.similarity) * 100)}% rewritten)`
        : 'Sent as approved');
      setDismissed(true);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof det === 'object') {
        const types = (det.validation_failures || []).map(f => f.type).join(', ');
        if (window.confirm(
          `🛡 Outbound validator blocked this reply.\n\n` +
          `Issues: ${types}\n\n` +
          `Edit the message first (recommended), OR send anyway (override)?\n\n` +
          `Click OK to override and send as-is. Cancel to edit instead.`
        )) {
          return approveDraft(finalText, true);
        }
        setEditing(true);
        toast.error('Please edit the highlighted fields before sending.');
      } else {
        toast.error(det || 'Send failed');
      }
    } finally { setLoadingAction(null); }
  };
  const rejectDraft = async () => {
    setLoadingAction('reject');
    try {
      await api.post(`/ai/drafts/${draft.id}/reject`, { reason: '' });
      toast.message('Draft rejected — no message sent');
      setDraft(null); setEditing(false);
    } catch (e) {
      toast.error('Could not reject');
    } finally { setLoadingAction(null); }
  };

  if (dismissed) return null;

  return (
    <div className="fab-card p-4" data-testid={`inbox-row-${conv.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0">
          <div className="font-semibold text-fab-text text-[13px]">{conv.customer_name || conv.customer_phone}</div>
          <div className="font-mono text-[11px] text-fab-text-muted">{conv.customer_phone}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {li.intent && (
            <span
              data-testid={`intent-chip-${conv.id}`}
              title={li.order_status_subtype ? `Sub-type: ${li.order_status_subtype}` : ''}
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${chipCls}`}
            >
              {li.intent}
              {typeof li.confidence === 'number' && <span className="ml-1 opacity-70">{li.confidence.toFixed(2)}</span>}
              {li.order_status_subtype && <span className="ml-1 opacity-80">· {li.order_status_subtype}</span>}
            </span>
          )}
          <div className="text-[10px] text-fab-error font-mono">{conv.last_routing_reason}</div>
        </div>
      </div>

      {!draft && (
        <div className="flex gap-2 items-center">
          <input
            data-testid={`inbox-reply-input-${conv.id}`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type a manual reply..."
            className="fab-input flex-1 px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
          />
          <button
            data-testid={`inbox-reply-send-${conv.id}`}
            onClick={() => { if (body) { onReply(conv.id, body); setBody(''); setDismissed(true); } }}
            className="btn-wa px-3 py-2 rounded-lg text-[12px] font-semibold"
          >Send</button>
          <button
            data-testid={`inbox-draft-btn-${conv.id}`}
            onClick={generateDraft}
            disabled={loadingAction === 'generate'}
            title={aiAssistable ? 'Generate AI draft suggestion' : 'AI draft available — intent classified as ' + (li.intent || 'UNCLEAR')}
            className="px-3 py-2 rounded-lg text-[12px] font-semibold border border-fab-primary text-fab-primary bg-white hover:bg-fab-primary-lighter disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {loadingAction === 'generate' ? '…' : '✨ AI Draft'}
          </button>
        </div>
      )}

      {draft && (
        <div className="mt-2 p-3 rounded-lg bg-fab-elevated border border-fab-primary-lighter" data-testid={`draft-panel-${conv.id}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold text-fab-primary uppercase tracking-wide">
              ✨ AI Draft Suggestion
              {draft.tool_calls_count > 0 && (
                <span className="ml-2 text-[10px] font-normal text-fab-text-muted">
                  ({draft.tool_calls_count} tool call{draft.tool_calls_count > 1 ? 's' : ''})
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              {draft.validation_ok === false && (
                <span
                  data-testid={`draft-validation-warn-${conv.id}`}
                  title={`Outbound validator flagged: ${draft.validation_failures?.map(f => f.type + ' ' + (f.token || f.value || '')).join(', ')}`}
                  className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-300 px-2 py-0.5 rounded-full"
                >🛡 VALIDATOR BLOCK ({draft.validation_failures?.length || 0})</span>
              )}
              {draft.validation_ok === true && (
                <span
                  data-testid={`draft-validation-ok-${conv.id}`}
                  className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 px-2 py-0.5 rounded-full"
                >🛡 verified</span>
              )}
              {draft.fallback && (
                <span className="text-[10px] text-fab-warning bg-orange-50 px-2 py-0.5 rounded">⚠ fallback</span>
              )}
            </div>
          </div>
          <textarea
            data-testid={`draft-textarea-${conv.id}`}
            value={draft.suggested_reply}
            onChange={(e) => setDraft({ ...draft, suggested_reply: e.target.value })}
            readOnly={!editing}
            rows={3}
            className={`w-full px-3 py-2 rounded-lg border text-[13px] resize-y ${editing ? 'border-fab-primary bg-white' : 'border-fab-primary-lighter bg-white text-fab-text-secondary'}`}
          />
          <div className="flex gap-2 mt-2 justify-end">
            <button
              data-testid={`draft-reject-${conv.id}`}
              onClick={rejectDraft}
              disabled={loadingAction !== null}
              className="px-3 py-1.5 rounded-lg text-[12px] font-semibold border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
            >Reject</button>
            {!editing ? (
              <button
                data-testid={`draft-edit-${conv.id}`}
                onClick={() => setEditing(true)}
                disabled={loadingAction !== null}
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold border border-fab-primary-lighter text-fab-text bg-white hover:bg-fab-elevated disabled:opacity-50"
              >Edit</button>
            ) : (
              <button
                data-testid={`draft-send-edit-${conv.id}`}
                onClick={() => approveDraft(draft.suggested_reply)}
                disabled={loadingAction !== null}
                className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-fab-primary text-white hover:bg-fab-primary-dark disabled:opacity-50"
              >Send Edit</button>
            )}
            {!editing && (
              <button
                data-testid={`draft-approve-${conv.id}`}
                onClick={() => approveDraft(draft.suggested_reply)}
                disabled={loadingAction !== null}
                className="btn-wa px-3 py-1.5 rounded-lg text-[12px] font-semibold disabled:opacity-50"
              >Approve & Send</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

